import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, Sparkles } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const HEIDELBERG_IMG =
  "https://images.unsplash.com/photo-1622214366189-72b19cc61597?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const CompassMark = () => (
  <svg
    viewBox="0 0 240 240"
    className="compass-svg w-full h-full"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <defs>
      <radialGradient id="g1" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#FFFFFF" />
        <stop offset="100%" stopColor="#F2F6F7" />
      </radialGradient>
      <linearGradient id="g2" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#2C7A8C" />
        <stop offset="100%" stopColor="#154C57" />
      </linearGradient>
    </defs>
    <circle cx="120" cy="120" r="112" fill="url(#g1)" stroke="#1F6B7A" strokeOpacity="0.15" strokeWidth="1.5" />
    <circle cx="120" cy="120" r="92" fill="none" stroke="#1F6B7A" strokeOpacity="0.12" strokeWidth="1" strokeDasharray="2 6" />
    {/* Cardinal markers */}
    {[0, 90, 180, 270].map((deg) => (
      <line
        key={deg}
        x1="120"
        y1="22"
        x2="120"
        y2="34"
        stroke="#1F6B7A"
        strokeOpacity="0.5"
        strokeWidth="2"
        transform={`rotate(${deg} 120 120)`}
      />
    ))}
    {/* Needle group rotates */}
    <g className="compass-needle">
      <polygon
        points="120,28 132,120 120,140 108,120"
        fill="url(#g2)"
      />
      <polygon
        points="120,212 108,120 120,100 132,120"
        fill="#E8A365"
        opacity="0.85"
      />
      <circle cx="120" cy="120" r="9" fill="#1F6B7A" />
      <circle cx="120" cy="120" r="3.5" fill="#FDFBF7" />
    </g>
  </svg>
);

const Hero = () => {
  const { t, dir } = useLanguage();

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative overflow-hidden pt-32 pb-6 md:pt-60 md:pb-10"
    >
      {/* Tinted background panel */}
      <div
        aria-hidden
        className="absolute inset-0 -z-20"
        style={{
          background:
            "linear-gradient(160deg, rgba(31,107,122,0.10) 0%, rgba(44,122,140,0.06) 40%, rgba(232,163,101,0.05) 100%)",
        }}
      />
      {/* Background grid */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10"
        style={{
          backgroundImage:
            "linear-gradient(rgba(31,107,122,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(31,107,122,0.08) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
          maskImage:
            "radial-gradient(ellipse at center, black 35%, transparent 75%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at center, black 35%, transparent 75%)",
        }}
      />

      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-12">
          {/* Text */}
          <div className="lg:col-span-7">
            <motion.span
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="eyebrow"
              data-testid="hero-eyebrow"
            >
              <Sparkles className="h-4 w-4" strokeWidth={1.5} />
              {t.hero.eyebrow}
            </motion.span>

            <motion.h1
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.05 }}
              className="display display-1 mt-5 text-[color:var(--kompass-ink)]"
              data-testid="hero-title"
            >
              {t.hero.title_1}{" "}
              <span className="brush-accent" style={{ color: "var(--kompass-primary-dark)" }}>
                {t.hero.title_accent}
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="mt-6 max-w-2xl text-base md:text-lg leading-relaxed text-[color:var(--kompass-ink-soft)]"
              data-testid="hero-subtitle"
            >
              {t.hero.subtitle}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="mt-8 flex flex-wrap gap-3"
            >
              <button
                type="button"
                onClick={() =>
                  document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })
                }
                className="btn-primary"
                data-testid="hero-cta-primary"
              >
                {t.hero.cta_primary}
                <ArrowRight
                  className={`h-4 w-4 ${dir === "rtl" ? "flip-x" : ""}`}
                  strokeWidth={2}
                />
              </button>
              <button
                type="button"
                onClick={() =>
                  document.getElementById("services")?.scrollIntoView({ behavior: "smooth" })
                }
                className="btn-secondary"
                data-testid="hero-cta-secondary"
              >
                {t.hero.cta_secondary}
              </button>
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.35 }}
              className="mt-12 grid grid-cols-3 gap-4 max-w-xl border-t border-[rgba(31,107,122,0.12)] pt-7"
              data-testid="hero-stats"
            >
              {[
                { n: "4", l: t.hero.stat_1 },
                { n: "10+", l: t.hero.stat_2 },
                { n: "500+", l: t.hero.stat_3 },
              ].map((s, idx) => (
                <div key={idx} className="text-start">
                  <div
                    className="display text-3xl md:text-4xl font-semibold"
                    style={{ color: "var(--kompass-primary-dark)" }}
                  >
                    {s.n}
                  </div>
                  <div className="text-xs md:text-sm text-[color:var(--kompass-muted)] mt-1">
                    {s.l}
                  </div>
                </div>
              ))}
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.5 }}
              className="mt-7 inline-flex items-center gap-2 text-sm text-[color:var(--kompass-ink-soft)]"
            >
              <MapPin className="h-4 w-4" style={{ color: "var(--kompass-primary)" }} />
              Eppelheimer Str. 21 · 69115 Heidelberg
            </motion.div>
          </div>

          {/* Visual */}
          <div className="lg:col-span-5">
            <div className="relative">
              <motion.div
                initial={{ opacity: 0, scale: 0.94 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="relative aspect-square w-full max-w-[440px] mx-auto float-soft"
              >
                <div
                  className="absolute inset-0 rounded-full"
                  style={{
                    background:
                      "radial-gradient(circle at 30% 30%, rgba(31,107,122,0.18), transparent 60%)",
                  }}
                />
                <CompassMark />
              </motion.div>

              {/* Floating Heidelberg image card */}
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.45 }}
                className="absolute -bottom-6 start-0 hidden md:block w-56 rounded-2xl overflow-hidden shadow-[0_24px_60px_-30px_rgba(31,107,122,0.55)] ring-1 ring-[rgba(31,107,122,0.1)] bg-white"
              >
                <img
                  src={HEIDELBERG_IMG}
                  alt="Heidelberg"
                  className="h-32 w-full object-cover"
                />
                <div className="px-3 py-2.5">
                  <div className="text-[11px] uppercase tracking-widest text-[color:var(--kompass-muted)]">
                    Heidelberg
                  </div>
                  <div className="text-sm font-semibold text-[color:var(--kompass-ink)]">
                    Eppelheimer Str. 21
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
